import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'

export interface ILocation {
	_id: string
	[key: string]: any
	// add other specific fields here, e.g. name: string, projectId: string, coords: { lat: number; lng: number }, etc.
}

export const useLocationStore = defineStore('locations', () => {
	// --- STATE ---
	const locations = ref<ILocation[]>([])
	const currentLocation = ref<ILocation | null>(null)
	const loading = ref<Record<string, boolean>>({})
	const loadingMap = ref<Record<string, boolean>>({})
	const errorMap = ref<Record<string, string>>({})
	let listLocationsPromise: Promise<ILocation[]> | null = null

	// --- HELPERS ---

	const formatError = (error: any): string =>
		error?.response?.data?.msg || error?.response?.data || error.message || 'Unknown error'

	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loadingMap.value[key]) {
			throw new Error(`🟧 ${key} is already in progress (locationStore)`)
		}
		loadingMap.value[key] = true
		delete errorMap.value[key]
		try {
			return await fn()
		} catch (e: any) {
			if (e?.name === 'AbortError') {
				console.warn(`🟨 ${key} was aborted (locationStore)`)
				throw e
			}
			const message = formatError(e)
			console.error(`🟥 Error in ${key} (locationStore):`, e)
			errorMap.value[key] = message
			throw e
		} finally {
			delete loadingMap.value[key]
		}
	}

	// --- Getters ---

	const locationsMap = computed(() => {
		const map: Record<string, ILocation> = {}
		locations.value.forEach((location) => {
			map[location._id] = location
		})
		return map
	})

	const locationsByProjectId = computed(() => {
		const map: Record<string, ILocation[]> = {}
		locations.value.forEach((location) => {
			if (!map[location.projectId]) map[location.projectId] = []
			map[location.projectId].push(location)
		})
		return map
	})

	// const locationsByProject = (projectId: string) => {
	// 	return locations.value.filter((location) => location.projectId === projectId)
	// }

	// --- ACTIONS ---

	const listLocations = async () => {
		if (listLocationsPromise) return listLocationsPromise
		listLocationsPromise = withLoading('listLocations', async () => {
			console.log('🍍 listLocations')
			const resp = await http.getAuth(`/api/v2/locations`)
			console.log('locations', resp)
			locations.value = resp || []
			return resp || []
		})
		listLocationsPromise.finally(() => {
			listLocationsPromise = null
		})
		return listLocationsPromise
	}

	// async function listLocationsByProject(projectId: string) {
	// 	return withLoading('listLocationsByProject', async () => {
	// 		try {
	// 			const resp = await http.getAuth(`/api/v1/locations/list/${projectId}`)
	// 			// locations.value = resp || []
	// 			return resp || []
	// 		} catch (error: any) {
	// 			console.error(error)
	// 			throw new Error(formatError(error))
	// 		}
	// 	})
	// }

	const getLocation = async (id: string) =>
		withLoading(`getLocation-${id}`, async () => {
			if (!id) throw new Error('Location ID is required')
			currentLocation.value = null
			const resp = await http.getAuth(`/api/v2/location/${id}`)
			currentLocation.value = resp || null
			return resp || null
		})

	const addNewLocation = async (data: Partial<ILocation>) =>
		withLoading('addNewLocation', async () => {
			const newLoc = await http.postAuth(`/api/v2/location`, data)
			locations.value.unshift(newLoc)
			return newLoc
		})

	const updateLocation = (data: ILocation) =>
		withLoading(`updateLocation-${data._id}`, async () => {
			const updated = await http.putAuth(`/api/v2/location`, data)
			const idx = locations.value.findIndex((l) => l._id === updated._id)
			if (idx !== -1) locations.value[idx] = updated
			if (currentLocation.value?._id === updated._id) {
				currentLocation.value = updated
			}
			return updated
		})

	const deleteLocation = async (id: string) =>
		withLoading('deleteLocation', async () => {
			await http.deleteAuth<void>(`/api/v2/location/${id}`)
			// locations.value = locations.value.filter((l) => l._id !== id)
			// if (currentLocation.value?._id === id) {
			// 	currentLocation.value = null
			// }
			return true
		})

	const archiveLocation = async (id: string) =>
		withLoading('archiveLocation', async () => {
			const archived = await http.postAuth(`/api/v1/locations/archive/${id}`, {})
			const idx = locations.value.findIndex((l) => l._id === archived._id)
			if (idx !== -1) locations.value[idx] = archived
			if (currentLocation.value?._id === archived._id) {
				currentLocation.value = archived
			}
			return archived
		})

	// --- INITIAL LOAD ---
	const _init = async () => {
		await listLocations()
	}
	_init()

	return {
		// state
		locations,
		currentLocation,
		loading,
    loadingMap,
		// getters
		locationsMap,
		locationsByProjectId,
		// locationsByProject,
		// actions
		listLocations,
		// listLocationsByProject,
		getLocation,
		addNewLocation,
		updateLocation,
		deleteLocation,
		archiveLocation,
	}
})
